import type { EraId } from '../config/eras';
import { eraForDate } from './era';
import type { TrackData } from './types';

/**
 * Track modifiers: the plug-in point for everything the day's winner adds to
 * tomorrow's circuit (Phase 3): chaos elements (the Colossus, the cheese
 * avalanche, the train...) and customizations (their poster on the billboard
 * and the blimp).
 *
 * A modifier is plain data in the daily spec, so it can come from the server
 * and be part of what every client generates. Behaviour is looked up in a
 * registry:
 *  - sim:      deterministic hazards driven by the lap clock, never random mid-lap
 *  - validate: prove a clean lap is still possible with it in place
 *  - render:   (render/modifiers.ts) its meshes, animated from the same clock
 */
export interface ModifierInstance {
  type: string;
  /** Position along the lap as a fraction 0..1 (the winner taps it on the mini-map). */
  at?: number;
  side?: -1 | 1;
  /** The winner's name, stamped on the element (cheese wheels, statue...). */
  tag?: string;
  params?: Record<string, string | number | boolean | null>;
}

export interface DailySpec {
  date: string;
  /** Re-roll counter (dev panel); 0 for the real daily circuit. */
  salt: number;
  /** The day's car. */
  era: EraId;
  /** The winner's name for the circuit, or null for a generated one. */
  name: string | null;
  modifiers: ModifierInstance[];
}

/** An obstacle in track coordinates: arc length s, lateral d, radius r. */
export interface TrackObstacle {
  s: number;
  d: number;
  r: number;
}

/** What a modifier can do inside the simulation. Pure functions of the lap clock. */
export interface SimModifierHooks {
  /** Obstacles present at this tick of the run (e.g. a stomping foot, a train). */
  obstacles?(tick: number): readonly TrackObstacle[];
}

export interface ModifierDefinition {
  type: string;
  category: 'chaos' | 'customization';
  label: string;
  /** False until the phase that builds it. */
  implemented: boolean;
  sim?(inst: ModifierInstance, track: TrackData): SimModifierHooks;
  /** Return a reason if this placement would make a clean lap impossible. */
  validate?(inst: ModifierInstance, track: TrackData): string | null;
}

const chaos = (type: string, label: string): ModifierDefinition => ({ type, category: 'chaos', label, implemented: false });

export const MODIFIERS: Record<string, ModifierDefinition> = {
  billboard: { type: 'billboard', category: 'customization', label: 'Winner poster on the big billboard', implemented: true },
  blimp: { type: 'blimp', category: 'customization', label: 'Vintage blimp with the winner poster', implemented: true },
  colossus: chaos('colossus', 'The Colossus'),
  'mega-jump': chaos('mega-jump', 'Mega jump'),
  cheese: chaos('cheese', 'Cheese avalanche'),
  train: chaos('train', 'The train'),
  town: chaos('town', 'Through the town'),
  sheep: chaos('sheep', 'The sheep'),
  'cliff-split': chaos('cliff-split', 'Cliff split'),
  loop: chaos('loop', 'Loop-the-loop'),
};

/** Phase 1: no winner yet, so a clean race with the house poster. */
export function defaultSpec(date: string, salt = 0): DailySpec {
  return {
    date,
    salt,
    era: eraForDate(date),
    name: null,
    modifiers: [{ type: 'billboard' }, { type: 'blimp' }],
  };
}

export function simHooksFor(spec: DailySpec, track: TrackData): SimModifierHooks[] {
  const hooks: SimModifierHooks[] = [];
  for (const inst of spec.modifiers) {
    const def = MODIFIERS[inst.type];
    if (def?.implemented && def.sim) hooks.push(def.sim(inst, track));
  }
  return hooks;
}

export function validateModifiers(spec: DailySpec, track: TrackData): string | null {
  for (const inst of spec.modifiers) {
    const def = MODIFIERS[inst.type];
    if (!def) return `unknown modifier ${inst.type}`;
    const reason = def.validate?.(inst, track);
    if (reason) return reason;
  }
  return null;
}
